import {
  type OpenChangeReason,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { type SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import type { DrawerRootPropsInterface } from '../types/drawer-root.types';
import type {
  DrawerFloatingContextInterface,
  UseDrawerReturnInterface,
} from '../types/use-drawer.types';
import { useDrawerRegistry } from './use-drawer-registry';
import { OVERLAY_TRANSITION_DURATION_MS } from '../../../utils/motion';

export function useDrawer(props: Partial<DrawerRootPropsInterface>): {
  drawer: UseDrawerReturnInterface;
  floating: DrawerFloatingContextInterface;
} {
  const {
    // Unique ID for drawer registry
    multiDrawerTriggerId,

    // Open state management props
    defaultOpen,
    open: controlledOpen,
    onOpenChange: setControlledOpen,

    // Performance optimization props
    shouldAwaitInteractionResponse = true,

    // Floating UI hook props
    useClickProps,
    useDismissProps,
    useRoleProps,
  } = props;

  const { isActive, close } = useDrawerRegistry();

  /**
   * Accessibility IDs for `Drawer.Header` and `Drawer.Body`
   */
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = 'open' in props;
  const open = isControlled ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for drawer state management
   */
  const { context, refs } = useFloating({
    open,
    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateOpenState(state, event, reason);
    },
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */

  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  /*
   * Handle dismiss behavior with enhanced outside press detection
   * Prevents closing when clicking on:
   * - Any Drawer.Content (handles multiple overlaid drawers)
   * - Floating UI elements (DatePicker, Combobox, Dropdown, etc.)
   */
  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) => {
      const target = event.target as HTMLElement;

      /*
       * Check if click is inside any Drawer.Content
       * This handles multiple adjacent drawers (multiDrawerTriggerId pattern)
       */
      if (target.closest('[data-c11n-component="Drawer.Content"]')) {
        return false;
      }

      /*
       * Check if click is inside any floating component's content.
       *
       * This prevents the drawer from closing when interacting with portal-rendered
       * floating content (DatePicker calendars, Combobox dropdowns, Dropdown menus,
       * Popover content, Menu items, Tooltip content, etc.).
       *
       * These components render their content via FloatingPortal to document.body,
       * making them technically "outside" the Drawer's DOM hierarchy. Without this check,
       * clicks on these elements would incorrectly trigger drawer dismissal.
       *
       * We detect floating content by checking for "ContentPositioner" in the component
       * name, which is the pattern used by all Constellation components that render
       * floating content via Floating UI.
       */
      if (target.closest('[data-c11n-component*="ContentPositioner"]')) {
        return false;
      }

      /*
       * Click is outside - allow dismissal
       */
      return true;
    },
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'dialog',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss, role]);

  /*
   * Keep the portal mounted for the exit transition
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, status } = useTransitionStatus(context, {
    duration: reducedMotion ? 0 : OVERLAY_TRANSITION_DURATION_MS,
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (!isControlled) {
        setUncontrolledOpen(newValue);
      }
      setControlledOpen?.(newValue, event, reason);

      // Close multi-trigger drawer
      if (multiDrawerTriggerId && newValue === false) {
        close(multiDrawerTriggerId);
      }
    },
    [isControlled, setControlledOpen, close, multiDrawerTriggerId],
  );

  /**
   * Handles `Drawer.Close` and `Drawer.CloseButton` click actions.
   *
   * @param event - Event object
   */
  const onClickClose = useCallback(
    async (event: SyntheticEvent) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      // Update drawer state
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState, shouldAwaitInteractionResponse],
  );

  /**
   * Open drawer if `multiDrawerTriggerId` exists and is active in registry
   */
  useEffect(() => {
    if (!open && multiDrawerTriggerId && isActive(multiDrawerTriggerId)) {
      updateOpenState(true);
    }
  }, [open, multiDrawerTriggerId, isActive, updateOpenState]);

  /**
   * Returns all necessary state and handlers for the drawer
   */
  const drawer = useMemo(
    () => ({
      // Open state props and handlers
      open,
      isMounted,
      onClickClose,

      // Trigger and reference refs
      refs,

      // Accessibility props and handlers
      multiDrawerTriggerId,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,

      // Trigger and reference props
      getReferenceProps,
    }),
    [
      open,
      isMounted,
      onClickClose,
      refs,
      multiDrawerTriggerId,
      labelId,
      descriptionId,
      getReferenceProps,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * Drawer.Content pays for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps, status };

  return { drawer, floating };
}
